import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { UiSelect } from '../components/UiSelect';
import toast from 'react-hot-toast';
import {
  Wallet, Plus, Edit, Trash2, TrendingUp, IndianRupee, Receipt, BarChart3, ArrowDownRight, ArrowUpRight,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { SkeletonTable, EmptyState } from '../components/LoadingStates';
import { expenseService } from '../services/expense';
import { Expense, CreateExpenseRequest, EXPENSE_CATEGORIES, EXPENSE_PAYMENT_MODES } from '../types/expense';
import { Modal, Field, ModalActions, Th, inputCls } from './inventory/StockItemsTab';
import { formatCurrency, formatDate } from './inventory/inventoryUtils';

type View = 'expenses' | 'pnl';

interface ExpenseFormData {
  category: string;
  description?: string;
  amount: number;
  paymentMode?: string;
  payee?: string;
  date: string;
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthAgoStr = () => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

export const Expenses: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;

  const [view, setView] = useState<View>('expenses');
  const [start, setStart] = useState(monthAgoStr());
  const [end, setEnd] = useState(todayStr());

  if (!org) {
    return (
      <div className="p-6">
        <EmptyState icon={<Wallet className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No organization selected" description="Your account is not linked to an organization." />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3.5 mb-6">
        <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
          <Wallet className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Expenses &amp; Profit</h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Track operating expenses and see your profit &amp; loss</p>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {([['expenses', 'Expenses', Receipt], ['pnl', 'Profit & Loss', BarChart3]] as [View, string, any][]).map(([key, label, Icon]) => (
          <button key={key} onClick={() => setView(key)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
            style={{
              backgroundColor: view === key ? currentTheme.colors.primary[600] : currentTheme.colors.background.card,
              color: view === key ? '#fff' : currentTheme.colors.text.secondary,
              borderColor: view === key ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
            }}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>From</label>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>To</label>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
      </div>

      {view === 'expenses'
        ? <ExpensesList org={org} restaurantId={restaurantId} start={start} end={end} />
        : <PnlView org={org} restaurantId={restaurantId} start={start} end={end} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Expenses list + CRUD
// ---------------------------------------------------------------------------
const ExpensesList: React.FC<{ org: string; restaurantId?: string; start: string; end: string }> = ({ org, restaurantId, start, end }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; id: string; label: string }>({ open: false, id: '', label: '' });

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<ExpenseFormData>();

  const { data: expenses, isLoading } = useQuery(
    ['expenses', org, restaurantId, start, end],
    () => expenseService.getExpenses({ org, restaurantId, start, end }),
  );

  const createMutation = useMutation(expenseService.createExpense, {
    onSuccess: () => { queryClient.invalidateQueries(['expenses']); close(); toast.success('Expense added'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to add expense'); },
  });
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateExpenseRequest> }) => expenseService.updateExpense(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['expenses']); close(); toast.success('Expense updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update expense'); },
    },
  );
  const deleteMutation = useMutation(expenseService.deleteExpense, {
    onSuccess: () => { queryClient.invalidateQueries(['expenses']); toast.success('Expense deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete expense'); },
  });

  const total = useMemo(() => (expenses ?? []).reduce((s, e) => s + (e.amount || 0), 0), [expenses]);

  function close() { setIsFormOpen(false); setEditing(null); reset(); }
  function openCreate() { setEditing(null); reset({ category: EXPENSE_CATEGORIES[0], date: todayStr(), paymentMode: 'CASH' }); setIsFormOpen(true); }
  function openEdit(ex: Expense) {
    setEditing(ex);
    reset({ category: ex.category, description: ex.description, amount: ex.amount, paymentMode: ex.paymentMode, payee: ex.payee, date: ex.date });
    setIsFormOpen(true);
  }

  const onSubmit = (data: ExpenseFormData) => {
    const payload: CreateExpenseRequest = {
      ...data,
      amount: Number(data.amount),
      organizationCode: org,
      ...(restaurantId && { restaurantId }),
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
          Total: <span className="font-semibold" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(total)}</span>
        </div>
        <button onClick={openCreate} className="inline-flex items-center btn-primary">
          <Plus className="h-5 w-5 mr-2" /> Add Expense
        </button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} columns={6} />
      ) : !expenses || expenses.length === 0 ? (
        <EmptyState
          icon={<Receipt className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No expenses in this period"
          description="Record rent, salaries, utilities and other costs to track your true profit."
          action={<button onClick={openCreate} className="inline-flex items-center btn-primary"><Plus className="h-5 w-5 mr-2" /> Add Expense</button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>Date</Th><Th>Category</Th><Th>Payee</Th><Th>Description</Th><Th>Mode</Th><Th className="text-right">Amount</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {expenses.map(ex => (
                <tr key={ex.id}>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{formatDate(ex.date)}</td>
                  <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{ex.category}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{ex.payee || '—'}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{ex.description || '—'}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{ex.paymentMode || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(ex.amount)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(ex)} className="p-2 rounded-lg" style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[50] }}><Edit className="h-4 w-4" /></button>
                      <button onClick={() => setConfirm({ open: true, id: ex.id, label: `${ex.category} · ${formatCurrency(ex.amount)}` })} className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isFormOpen && (
        <Modal title={editing ? 'Edit Expense' : 'Add Expense'} onClose={close} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Category" required>
                <input list="expense-categories" {...register('category', { required: 'Category is required' })} className={inputCls} placeholder="e.g., Rent" />
                <datalist id="expense-categories">{EXPENSE_CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
              </Field>
              <Field label="Amount (₹)" error={errors.amount?.message} required>
                <input type="number" step="any" min="0" {...register('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Must be > 0' } })} className={inputCls} placeholder="0.00" />
              </Field>
              <Field label="Date" required>
                <input type="date" {...register('date', { required: true })} className={inputCls} />
              </Field>
              <Field label="Payment Mode">
                <Controller name="paymentMode" control={control} render={({ field }) => (
                  <UiSelect value={field.value || ''} onChange={field.onChange}
                    options={EXPENSE_PAYMENT_MODES.map(m => ({ value: m, label: m }))} />
                )} />
              </Field>
              <Field label="Payee / Vendor"><input {...register('payee')} className={inputCls} placeholder="optional" /></Field>
            </div>
            <Field label="Description"><textarea {...register('description')} rows={2} className={inputCls} placeholder="optional" /></Field>
            <ModalActions onCancel={close} loading={createMutation.isLoading || updateMutation.isLoading}
              submitLabel={editing ? 'Update' : 'Add Expense'} loadingLabel={editing ? 'Updating...' : 'Adding...'} />
          </form>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={() => { deleteMutation.mutate(confirm.id); setConfirm(c => ({ ...c, open: false })); }}
        title="Delete Expense" message={`Delete "${confirm.label}"? This cannot be undone.`}
        confirmText="Delete" type="danger" isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// P&L view
// ---------------------------------------------------------------------------
const PnlView: React.FC<{ org: string; restaurantId?: string; start: string; end: string }> = ({ org, restaurantId, start, end }) => {
  const { currentTheme } = useTheme();
  const { data: pnl, isLoading } = useQuery(
    ['pnl', org, restaurantId, start, end],
    () => expenseService.getPnl(org, restaurantId, start, end),
  );

  if (isLoading) return <SkeletonTable rows={6} columns={2} />;
  if (!pnl) return <EmptyState icon={<BarChart3 className="h-14 w-14" style={{ color: currentTheme.colors.text.secondary }} />} title="No data" description="Try a different period." />;

  const profitColor = pnl.netProfit >= 0 ? '#16a34a' : '#dc2626';

  return (
    <div className="space-y-6">
      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={<IndianRupee className="h-5 w-5" />} label="Net Sales" value={formatCurrency(pnl.netSales)} bg="#eef2ff" fg="#4f46e5" sub={`${pnl.orderCount} orders`} />
        <Card icon={<ArrowDownRight className="h-5 w-5" />} label="COGS" value={formatCurrency(pnl.cogs)} bg="#fef3c7" fg="#b45309" sub="ingredients used" />
        <Card icon={<ArrowDownRight className="h-5 w-5" />} label="Expenses" value={formatCurrency(pnl.expenseTotal)} bg="#fee2e2" fg="#dc2626" sub="operating costs" />
        <Card icon={pnl.netProfit >= 0 ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownRight className="h-5 w-5" />} label="Net Profit" value={formatCurrency(pnl.netProfit)} bg="#dcfce7" fg={profitColor} sub={pnl.netSales > 0 ? `${((pnl.netProfit / pnl.netSales) * 100).toFixed(1)}% margin` : ''} />
      </div>

      {/* P&L statement */}
      <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] overflow-hidden" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
        <div className="px-5 py-3 border-b font-semibold" style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}>
          Profit &amp; Loss Statement
        </div>
        <div className="p-5 space-y-2">
          <PnlRow label="Gross Sales" value={pnl.grossSales} />
          <PnlRow label="Less: GST collected" value={-pnl.gstCollected} muted />
          <PnlRow label="Net Sales" value={pnl.netSales} bold divider />
          <PnlRow label="Less: Cost of Goods Sold" value={-pnl.cogs} muted />
          <PnlRow label="Gross Profit" value={pnl.grossProfit} bold divider />
          <PnlRow label="Less: Operating Expenses" value={-pnl.expenseTotal} muted />
          <PnlRow label="Net Profit" value={pnl.netProfit} bold divider strong />
        </div>
      </div>

      {/* Expense breakdown */}
      {pnl.expensesByCategory.length > 0 && (
        <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] overflow-hidden" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <div className="px-5 py-3 border-b font-semibold flex items-center gap-2" style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}>
            <TrendingUp className="h-4 w-4" /> Expenses by Category
          </div>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <tbody className="ui-tbody">
              {pnl.expensesByCategory.map(c => {
                const pct = pnl.expenseTotal > 0 ? (c.amount / pnl.expenseTotal) * 100 : 0;
                return (
                  <tr key={c.category}>
                    <td className="px-5 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{c.category}</td>
                    <td className="px-5 py-3 w-1/2">
                      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme.colors.background.hover }}>
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: currentTheme.colors.primary[500] }} />
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-right font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(c.amount)}</td>
                    <td className="px-5 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.secondary }}>{pct.toFixed(0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
        COGS is estimated from inventory consumed (recipe deductions &amp; wastage) valued at current cost. Net Sales excludes GST.
      </p>
    </div>
  );
};

const Card: React.FC<{ icon: React.ReactNode; label: string; value: string; bg: string; fg: string; sub?: string }> = ({ icon, label, value, bg, fg, sub }) => {
  const { currentTheme } = useTheme();
  return (
    <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-lg flex-shrink-0" style={{ backgroundColor: bg, color: fg }}>{icon}</div>
        <div className="min-w-0">
          <p className="text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{label}</p>
          <p className="text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>{value}</p>
        </div>
      </div>
      {sub && <p className="text-xs mt-1.5" style={{ color: currentTheme.colors.text.secondary }}>{sub}</p>}
    </div>
  );
};

const PnlRow: React.FC<{ label: string; value: number; bold?: boolean; muted?: boolean; divider?: boolean; strong?: boolean }> = ({ label, value, bold, muted, divider, strong }) => {
  const { currentTheme } = useTheme();
  const color = strong ? (value >= 0 ? '#16a34a' : '#dc2626') : muted ? currentTheme.colors.text.secondary : currentTheme.colors.text.primary;
  return (
    <div className={clsx('flex justify-between text-sm', divider && 'pt-2 mt-1 border-t', bold && 'font-semibold', strong && 'text-base')}
      style={{ borderColor: currentTheme.colors.border.primary }}>
      <span style={{ color: muted ? currentTheme.colors.text.secondary : currentTheme.colors.text.primary }}>{label}</span>
      <span style={{ color }}>{formatCurrency(value)}</span>
    </div>
  );
};
